import { Carousel } from 'react-responsive-carousel'
import { Button } from '../../../components/button'
import { useAuth } from '../../../contexts/auth'
import { useCallback, useState } from 'react'
import Router from 'next/router'
import { ROUTES } from '../../../constants'
import { VIEWS as AUTH_VIEWS } from '../../auth/modals'
import { Select } from '../../../components/inputs'
import useFilter from './hooks/use-filter'

// ----------------------------------------------------------------------

const indicatorStyles = {
  background: 'var(--common-white)',
  width: 14,
  height: 14,
  display: 'inline-block',
  margin: '0 1.3rem',
  borderRadius: '50%',
  opacity: 0.37,
  cursor: 'pointer',
}

const renderIndicator = (onClickHandler, isSelected, index, label) => {
  if (isSelected) {
    return (
      <li
        style={{ ...indicatorStyles, opacity: 1 }}
        aria-label={`Selected: ${label} ${index + 1}`}
        title={`Selected: ${label} ${index + 1}`}
      />
    )
  }
  return (
    <li
      style={indicatorStyles}
      onClick={onClickHandler}
      onKeyDown={onClickHandler}
      value={index}
      key={index}
      role='button'
      tabIndex={0}
      title={`${label} ${index + 1}`}
      aria-label={`${label} ${index + 1}`}
    />
  )
}

const CarouselItem = ({ title, subTitle, actionButton, backgroundImageSrc }) => {
  return (
    <div className='root'>
      <div className='hero-container'>
        <div className='hero-image hero-image-left'>
          <div className='hero-content hero-content-desktop'>
            <h1>{title}</h1>
            <p>{subTitle}</p>
            {actionButton}
          </div>
        </div>
        <div className='hero-image hero-image-right'>
          <div className='hero-content hero-content-mobile'>
            <h1>{title}</h1>
            <p>{subTitle}</p>
            {actionButton}
          </div>
        </div>
      </div>

      <style jsx>{`
        .root {
          .hero-container {
            height: 63.5rem;
            width: 100%;
            display: grid;
            grid-template-columns: 1fr 1fr;

            .hero-content {
              color: var(--common-white);
              padding: 10rem 20rem 5rem;
              text-align: start;

              h1 {
                font-size: 5.4rem;
                font-weight: 700;
                margin-bottom: 1rem;
                line-height: 1.2;
              }
              p {
                font-size: 2.2rem;
                margin-bottom: 2rem;
                line-height: 1.4;
              }
              .button-container {
                margin-top: 2rem;
              }
            }

            .hero-content-mobile {
              display: none;
            }

            .hero-image {
              height: 100%;
              width: 100%;
              background-position: center center;
              background-repeat: no-repeat;
              background-size: cover;
              background-color: var(--primary-text-color);
              opacity: 1;
            }

            .hero-image-left {
              background-image: url('/images/home/hero/image_0@2x.jpg');
            }

            .hero-image-right {
              background-image: url('/images/home/hero/image_1@2x.jpg');
            }
          }
        }

        @media (max-width: 1920px) {
          .root {
            .hero-container {
              .hero-content {
                padding: 10rem 10rem 5rem;
              }
            }
          }
        }

        @media (max-width: 1536px) {
          .root {
            .hero-container {
              .hero-content {
                padding: 10rem 7rem 5rem;

                h1 {
                  font-size: 5rem;
                }
                p {
                  font-size: 2.1rem;
                }
              }
            }
          }
        }

        @media (max-width: 1200px) {
          .root {
            .hero-container {
              .hero-content {
                padding: 9rem 6rem 3rem;

                h1 {
                  font-size: 4.5rem;
                }
                p {
                  font-size: 2rem;
                }
              }
            }
          }
        }

        @media (max-width: 1024px) {
          .root {
            .hero-container {
              height: 45rem;
              grid-template-columns: 1fr;

              .hero-content {
                padding: 4rem 5rem;

                h1 {
                  margin-bottom: 0.5rem;
                }
                p {
                  line-height: 1.6;
                  margin-bottom: 1rem;
                }
                .button-container {
                  margin-top: 2rem;
                }
              }

              .hero-image-left {
                display: none;
              }

              .hero-content-desktop {
                display: none;
              }

              .hero-content-mobile {
                display: block;
              }
            }
          }
        }

        @media (max-width: 600px) {
          .root {
            .hero-container {
              height: calc(100vh - 30rem);

              .hero-content {
                display: flex;
                flex-direction: column;
                justify-content: center;
                height: 100%;
                padding: 3rem;

                h1 {
                  font-size: 2rem;
                  margin-bottom: 0.5rem;
                }
                p {
                  font-size: 1.5rem;
                  line-height: 1.6;
                  margin-bottom: 1rem;
                }
                .button-container {
                  margin-top: 2rem;
                }
              }
            }
          }
        }

        @media (max-width: 300px) {
          .root {
            .hero-container {
              height: calc(100vh - 22rem);
              min-height: 45rem;
              .hero-content {
                h1 {
                  font-size: 2rem;
                }
                p {
                  font-size: 1.4rem;
                  line-height: 1.6;
                }
              }
            }
          }
        }
      `}</style>

      <style jsx>{`
        .root {
          .hero-container {
            .hero-image-right {
              background-image: url(${backgroundImageSrc[1]});
            }
          }
        }

        @media (max-width: 1024px) {
          .root {
            .hero-container {
              .hero-image-right {
                background-image: linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)),
                  url(${backgroundImageSrc[0]});
              }
            }
          }
        }
      `}</style>
    </div>
  )
}

const HeroSection = () => {
  const [{ isAuthenticated, userDetails }, { setAuthModal }] = useAuth()
  const { verified } = userDetails

  const {
    selectedMake,
    setSelectedMake,
    makes,
    normalizedMakes,
    isMakesLoading,
    defaultMakeOption,
    selectedModel,
    setSelectedModel,
    models,
    normalizedModels,
    isModelsLoading,
    defaultModelOption,
  } = useFilter()

  const [keyword, setKeyword] = useState('')

  const handleMakeChange = useCallback(
    ({ value }) => {
      setSelectedMake({ value, label: normalizedMakes[value] })

      if (value === defaultMakeOption.value) {
        setSelectedModel(defaultModelOption)
      }
    },
    [defaultMakeOption, defaultModelOption, normalizedMakes, setSelectedMake, setSelectedModel],
  )

  const handleModelChange = useCallback(
    ({ value }) => {
      setSelectedModel({ value, label: normalizedModels[value] })
    },
    [normalizedModels, setSelectedModel],
  )

  const handleSearchClick = useCallback(() => {
    let resultOptions = {}

    if (keyword?.trim()) {
      resultOptions = { ...resultOptions, searchTerms: keyword.trim() }
    }

    if (selectedMake?.value !== 0) {
      const make = {
        [selectedMake.value]: {
          id: selectedMake.value,
          name: selectedMake.label,
        },
      }

      resultOptions = { ...resultOptions, makes: JSON.stringify(make) }
    }

    if (selectedModel?.value !== 0) {
      const model = {
        [selectedModel.label.toLowerCase()]: {
          id: selectedModel.value,
          name: selectedModel.label,
        },
      }

      resultOptions = { ...resultOptions, models: JSON.stringify(model) }
    }

    const params = new URLSearchParams(resultOptions)
    const url = `${ROUTES.Search}?${params}`

    Router.push(url)
  }, [keyword, selectedMake, selectedModel])

  const handleSignUpClick = useCallback(() => {
    if (isAuthenticated) {
      Router.push(ROUTES.Account)
    } else setAuthModal({ open: true, view: AUTH_VIEWS.Login })
  }, [isAuthenticated, setAuthModal])

  const handleGetVerifiedClick = useCallback(() => {
    if (!isAuthenticated) {
      return setAuthModal({ open: true, view: AUTH_VIEWS.Login })
    }

    if (!verified) {
      return Router.push(ROUTES.GetVerified)
    }

    return Router.push(ROUTES.Account)
  }, [isAuthenticated, setAuthModal, verified])

  const handleGetStartedClick = useCallback(() => {
    if (isAuthenticated) {
      Router.push(ROUTES.Account)
    } else setAuthModal({ open: true, view: AUTH_VIEWS.Login })
  }, [isAuthenticated, setAuthModal])

  return (
    <div className='root'>
      {/* <Carousel
        autoPlay
        infiniteLoop
        showArrows={false}
        showStatus={false}
        renderIndicator={renderIndicator}
        showThumbs={false}
        interval={8000}
      >
        <CarouselItem
          title='privauto the one-stop vehicle shop for everyone'
          subTitle='We provide an easy, safe, and transparent way for anyone to buy and sell a vehicle privately. Our concierge walks you through the entire process offering buyers financing, protection, and transportation options. If you are selling we pay you on behalf of the buyer. We took a complex problem and made it easy and stress free!'
          actionButton={<Button onClick={handleSignUpClick}>Get Started</Button>}
          backgroundImageSrc={['/images/home/hero/image_1.jpg', '/images/home/hero/image_1@2x.jpg']}
        />

        <CarouselItem
          title='Buyers and Sellers are verified and anonymous'
          subTitle='Most people wishing to buy or sell privately are justifiably concerned about conducting a transaction with a stranger. At privauto, we protect both parties by verifying all identities while maintaining their anonymity through the platform. Our concierge will be your only point of contact.'
          actionButton={<Button onClick={handleGetVerifiedClick}>Get Started</Button>}
          backgroundImageSrc={['/images/home/hero/image_3.jpg', '/images/home/hero/image_3@2x.jpg']}
        />

        <CarouselItem
          title='Peace of mind with easy, Secure Transactions'
          subTitle="We take all of the risk away from buying or selling a vehicle privately. We make it safe and simple by guaranteeing your money and your new vehicle's title."
          actionButton={<Button onClick={handleGetStartedClick}>Get Started</Button>}
          backgroundImageSrc={['/images/home/hero/image_2.jpg', '/images/home/hero/image_2@2x.jpg']}
        />
      </Carousel> */}

        <div className="heroRoot">
          <div className="heroBg">
            <div className="overlay">
              <div className="container">
                <div className="top">
                  <div className="copy">
                    <h1>
                      Buy &amp; Sell Cars
                      <br />
                      Privately. Safely.
                      <br />
                      Stress-Free.
                    </h1>

                    <p>
                      Privauto is the <b> secure &amp; easy way </b> to buy and sell vehicles peer-to-peer with no middleman fees
                      or pressure from dealerships.
                    </p>

                    <div className="searchBar" role="search" aria-label="Search cars">
                      <div className="selectShell selectShell-left">
                        <Select
                          isLoading={isMakesLoading}
                          aria-label="makes"
                          id="home-makes"
                          instanceId="home-makes"
                          name="makes"
                          placeholder="All Makes"
                          value={selectedMake}
                          options={makes}
                          onChange={handleMakeChange}
                          classNamePrefix="hero-select"
                        />
                      </div>

                      <input
                        className="input"
                        type="text"
                        placeholder="Enter Model or Keyword"
                        aria-label="Enter Model or Keyword"
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                      />

                      <div className="selectShell selectShell-right">
                        <Select
                          isLoading={isModelsLoading}
                          isDisabled={selectedMake?.value === 0}
                          aria-label="models"
                          id="home-models"
                          instanceId="home-models"
                          name="models"
                          placeholder="All Models"
                          value={selectedModel}
                          options={models}
                          onChange={handleModelChange}
                          classNamePrefix="hero-select"
                        />
                      </div>

                      <button
                        className="go"
                        type="button"
                        aria-label="Search"
                        onClick={handleSearchClick}
                      >
                        <img src='/images/magnifying-glass.png' />
                      </button>
                    </div>

                    <div className="badges">
                      <div className="badge">
                        <span className="icon">
                          <img src='/images/flash.png' />
                        </span>
                        <div>
                          <div className="badgeTitle">Easy</div>
                          <div className="badgeSub">Guided, simple <br /> process</div>
                        </div>
                      </div>

                      <div className="badge">
                        <span className="icon">
                          <img src='/images/shield.png' />
                        </span>
                        <div>
                          <div className="badgeTitle">Safe</div>
                          <div className="badgeSub">Full-agent <br /> service &amp; <br /> vetted buyers</div>
                        </div>
                      </div>

                      <div className="badge">
                        <span className="icon">
                          <img src='/images/credit-card.png' />
                        </span>
                        <div>
                          <div className="badgeTitle">Secure</div>
                          <div className="badgeSub">Secured payments <br /> &amp; protection</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <style jsx>{`
            .heroRoot {
              width: 100%;
              background: #0b1320;
            }

            .heroBg {
              min-height: 55vh;
              background-image: url('/images/home-background.png');
              background-size: cover;
              background-position: center;
              background-repeat: no-repeat;
              background-attachment: fixed;
              // background-color: #75768a;
              color: #fff;
            }

            .overlay {
              min-height: 55vh;
              background: linear-gradient(
                to bottom,
                rgba(29, 31, 40, 0.69),
                rgba(14, 15, 20, 0.69)
              );
              display: flex;
              align-items: flex-end;
            }

            .container {
              width: 100%;
              height: 100%;
              margin: 0 auto;
              padding: 0 4.2rem;
              max-width: 192rem;
              margin-bottom: 12rem;
              margin-top: 16rem;
            }

            .copy {
              max-width: 720px;
            }

            h1 {
              font-size: 58px;
              line-height: 1.15;
              margin: 0 0 2rem;
              font-weight: 800;
              letter-spacing: 1px;
            }

            p {
              margin: 0 0 4rem;
              font-size: 20px;
              line-height: 1.15;
              line-height: 1.6;
              max-width: 450px;
              letter-spacing: 1px;
              opacity: 0.8;
            }

            .searchBar {
              display: grid;
              grid-template-columns: 229px 324px 266px 94px;
              background: rgba(255, 255, 255, 0.12);
              align-items: stretch;
              overflow: visible;
            }

            .selectShell {
              min-width: 0;
              background: #fff;
              align-items: stretch;
              position: relative;
            }

            .selectShell-left {
              border-radius: 8px 0 0 8px;
              background-color: #eaecf3;
              overflow: visible;
            }

            .selectShell-right {
              border-left: 1px solid #dddbdb;
              overflow: visible;
            }

            :global(#home-makes) {
              height: 100%;
            }

            :global(#home-models) {
              height: 100%;
            }

            .input {
              border: 0;
              outline: 0;
              border-radius: 0;
              padding: 18px 14px;
              font-size: 20px;
              color: #111;
              background: rgba(255, 255, 255);
              border-right: 1px solid #dddbdb;
            }

            .go {
              border: 0;
              border-radius: 0 8px 8px 0;
              cursor: pointer;
              font-size: 24px;
              background: #1e73ff;
              color: #fff;
              width: 110px;
            }

            .go img {
              width: 22px;
              padding-top: 8px;
            }

            :global(.hero-select__control) {
              min-height: 100% !important;
              height: 100% !important;
              border: 0 !important;
              box-shadow: none !important;
              background: transparent !important;
            }

            :global(.selectShell-left .hero-select__control) {
              background: #eaecf3 !important;
            }

            :global(.hero-select__value-container) {
              height: 100% !important;
              padding: 0 14px !important;
            }

            :global(.hero-select__single-value) {
              color: #111 !important;
              font-size: 20px !important;
            }

            :global(.hero-select__placeholder) {
              color: #111 !important;
              font-size: 20px !important;
            }

            :global(.hero-select__input-container) {
              color: #111 !important;
              font-size: 20px !important;
              margin: 0 !important;
              padding: 0 !important;
            }

            :global(.hero-select__indicator-separator) {
              display: none !important;
            }

            :global(.hero-select__dropdown-indicator) {
              color: #333 !important;
              padding-right: 12px !important;
            }

            :global(.hero-select__menu) {
              margin-top: 4px !important;
              border-radius: 8px !important;
              overflow: hidden;
              z-index: 9999 !important;
              position: absolute !important;
            }

            :global(.hero-select__option) {
              font-size: 18px !important;
              color: var(--secondary-bg-color);
            }

            :global(.hero-select__control--is-disabled) {
              background: #f4f4f4 !important;
              opacity: 0.7;
            }

            .input {
              border: 0;
              outline: 0;
              border-radius: 0;
              padding: 18px 14px;
              font-size: 20px;
              color: #111;
              background: rgba(255, 255, 255);
              border-right: 1px solid #dddbdb;
            }

            .go {
              border: 0;
              border-radius: 0 8px 8px 0;
              cursor: pointer;
              font-size: 24px;
              background: #1e73ff;
              color: #fff;
              width: 110px;
            }

            .go img {
              width: 22px;
              padding-top: 8px;
            }

            .badges {
              display: flex;
              gap: 22px;
              margin-top: 3rem;
              flex-wrap: wrap;
            }

            .badge {
              display: flex;
              gap: 10px;
            }

            .icon {
              font-size: 30px;
            }

            .badgeTitle {
              font-weight: 700;
              font-size: 24px;
              margin-bottom: 6px;
            }

            .badgeSub {
              font-size: 18px;
              opacity: 0.7;
            }

            @media (max-width: 1024px) {
              .container {
                margin-bottom: 19rem;
                margin-top: 4rem;
              }
              .heroBg,
              .overlay {
                min-height: 720px;
              }
              .searchBar {
                grid-template-columns: 1fr;
              }
              .select-model {
                border-radius: 8px 8px 0 0;
                background-color: #eaecf3;
              }
              select {
                font-size: 18px;
              }
              .input {
                border-bottom: 1px solid #dddbdb;
                font-size: 18px;
              }
              h1 {
                font-size: 40px;
              }
              p {
                font-size: 16px;
                margin: 0 0 3rem;
              }
              .go {
                height: 44px;
                border-radius: 0 0 8px 8px;
                width: 100%;
              }

              :global(.hero-select__control) {
                border-radius: 8px 8px 0 0 !important;
              }

              :global(.hero-select__value-container) {
                padding: 18px 14px !important;
              }
            }

            @media (max-width: 480px) {
              .container {
                padding: 40px 16px 24px;
              }
              h1 {
                font-size: 32px;
              }
            }
          `}</style>
        </div>
    </div>
  )
}

export default HeroSection
